import { useState } from "react";
import config from "../../../config";
import {
  COORDINATOR,
  PARTICIPANT,
  REPRESENTATIVE,
  TEAM_ADMIN,
} from "../../../constants";
import { FundraiserForAccount } from "../../../Interfaces/AccountUsers";
import "./GeneralDashboardFundraiserDisplay.css";
import silhoutte from "../../../images/headshot-silhouette-30.png";
import {
  useHandleViewFundraiser,
  calculateProgressPercentage,
  getShareButtonText,
  displayFundraiserProfileUpload,
} from "../DashboardCommonFunctions";
import { ParticipantDashboardShareButton } from "../../../components/Storefront/ShareStoreComponent";
interface GeneralDashboardFundraiserDisplayProps {
  fundraisers: FundraiserForAccount[];
}

const GeneralDashboardFundraiserDisplay: React.FC<
  GeneralDashboardFundraiserDisplayProps
> = ({ fundraisers }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 2;
  const totalPages = Math.ceil(fundraisers.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const displayedFundraisers = fundraisers.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handleViewFundraiser = useHandleViewFundraiser();

  const FundraiserTag = ({
    fundraiser,
  }: {
    fundraiser: FundraiserForAccount;
  }) => {
    return (
      <span
        className={`general-dashboard-desktop-badge general-dashboard-desktop-badge-type-${fundraiser.fundraiser_type}`}
      >
        {fundraiser.fundraiser_type}
      </span>
    );
  };

  const CodeDisplay = ({
    fundraiser,
  }: {
    fundraiser: FundraiserForAccount;
  }) => {
    const buttonStyle: React.CSSProperties = {
      backgroundColor: "transparent",
      color: "blue",
      border: "none",
      padding: "0.5rem 1rem",
      fontSize: "1rem",
      cursor: "pointer",
    };

    return (
      <div className="general-dashboard-desktop-participant-code-section">
        <div className="general-dashboard-desktop-code-left">
          <p className="general-dashboard-desktop-code-label">
            {fundraiser.fundraiser_type === PARTICIPANT
              ? "Participant Code"
              : "Fundraiser Code"}
          </p>
          <p className="general-dashboard-desktop-code-value">
            {fundraiser.code}
          </p>
        </div>
        <ParticipantDashboardShareButton
          fundraiserName={fundraiser.display_name}
          participantName={fundraiser.display_name}
          fundraiserCode={fundraiser.code}
          buttonText={getShareButtonText(fundraiser, true)}
          style={buttonStyle}
        />
      </div>
    );
  };

  return (
    <div className="general-dashboard-desktop-fundraiser-container">
      <div className="general-dashboard-desktop-fundraiser-display">
        {displayedFundraisers.map((item) => {
          const progressPercent = calculateProgressPercentage(
            item.total_raised,
            item.total_goal,
          );

          return (
            <div
              // fundraiser id may be duplicated if multiple instances of a single fundraiser
              // IE are a coordinator and participant, need more specific to ensure uniqueness
              // just id and display name fail if they are a coordinator and team admin
              // just id and type fail if they have multiple participants
              key={`${item.fundraiser_id} - ${item.display_name} - ${item.fundraiser_type}`}
              className="general-dashboard-desktop-card"
            >
              {/* Header Section */}
              <div className="general-dashboard-desktop-card-header">
                <img
                  src={item.profile_picture || silhoutte}
                  alt={item.display_name || ""}
                  className="general-dashboard-desktop-avatar"
                />
                <div className="general-dashboard-desktop-card-title-section">
                  <div className="general-dashboard-desktop-card-name-badge">
                    <h3 className="general-dashboard-desktop-participant-name">
                      {item.display_name || ""}
                    </h3>
                    <FundraiserTag fundraiser={item} />
                  </div>
                  <p className="general-dashboard-desktop-name">
                    {item.org_name + " " + item.fundraiser_name}
                  </p>
                </div>
              </div>

              {/* Days Left */}
              <p className="general-dashboard-desktop-days-left">
                ● {item.days_left} Days Left
              </p>

              {/* Fundraising Goal Section */}
              <div className="general-dashboard-desktop-goal-section">
                <h4 className="general-dashboard-desktop-goal-label">
                  Fundraising Goal
                </h4>
                <div className="general-dashboard-desktop-progress-bar-container">
                  <div className="general-dashboard-desktop-progress-bar">
                    <div
                      className="general-dashboard-desktop-progress-bar-fill"
                      style={{ width: `${progressPercent}%` }}
                    ></div>
                  </div>
                  <p className="general-dashboard-desktop-goal-amount">
                    ${(item.total_raised ?? 0).toLocaleString()} / $
                    {(item.total_goal ?? 0).toLocaleString()}
                  </p>
                </div>
              </div>

              <CodeDisplay fundraiser={item} />

              {/* View My Sales Button */}
              <button
                onClick={() => handleViewFundraiser(item)}
                className="general-dashboard-desktop-view-sales-btn"
              >
                {item.fundraiser_type === PARTICIPANT
                  ? "📊 View My Sales"
                  : "Manage"}
              </button>
              {displayFundraiserProfileUpload({ fundraiser: item })}
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="general-dashboard-desktop-pagination">
          <button
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            className="general-dashboard-desktop-pagination-btn"
          >
            ← Previous
          </button>
          <span className="general-dashboard-desktop-pagination-info">
            Page {currentPage + 1} of {totalPages}
          </span>
          <button
            onClick={() =>
              setCurrentPage(Math.min(totalPages - 1, currentPage + 1))
            }
            disabled={currentPage === totalPages - 1}
            className="general-dashboard-desktop-pagination-btn"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default GeneralDashboardFundraiserDisplay;
